import { JsonInputType } from "@tosize/shapes/src/lib/models/Input/JsonInputType";
import type { Finish } from "@tosize/shapes/src/types/finish";
import { Alert, Button, DialogButton, Switch, formatNumber } from "@tosize/ui";
import isEqual from "lodash.isequal";
import { Tabs } from "radix-ui";
import { type FC, Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { usePostDataContext } from "../../../../context/PostDataContext";
import { useEdgeBands } from "../../../../hooks/useEdgeBands";
import useFinishes from "../../../../hooks/useFinishes";
import useSelectedFinishes from "../../../../hooks/useSelectedFinishes";
import { BackButton } from "../../../ui/BackButton";
import { SHAPE_DIMENSIONS_STEP_IDENTIFIER } from "../../Steps/ShapeDimensions";
import { TabsTrigger } from "../../TabsTrigger";
import { FinishOptionDrillHoles } from "../FinishOption/FinishOptionDrillHoles";
import FinishOptionMultiple from "../FinishOption/FinishOptionMultiple";
import FinishOptionSingle from "../FinishOption/FinishOptionSingle";

interface FinishesTabComponentProps {
	finishes: Finish[];
}

const FinishesTabComponent: FC<FinishesTabComponentProps> = ({ finishes }) => {
	const intl = useIntl();
	const { ungroupedFinishes, disabledByRightAngle } = useFinishes();
	const { applyEdgeBandOnSides } = useEdgeBands();
	const { workpiece, setWorkpiece } = usePostDataContext();
	const { reset, applyFinishOnSides, applyFinishOptionOnSides, applyFinishOptionParameterOnSides } =
		useSelectedFinishes();

	const hasWorkpieceFinishes = Object.keys(workpiece.finishes.side).length > 0;

	const defaultFinish = useMemo(() => {
		// if all finishes from initialState on all sides are the same select that finish by default,
		// with multiple finish types get the type with the most sides
		// if there are no finishes in initialState get first available finish

		const sides = Object.values(workpiece.finishes.side);

		if (sides.length) {
			const finishTypes = sides.map((side) => Object.keys(side)[0]);

			const mode = (arr: (string | undefined)[]) => {
				return arr
					.sort((a, b) => arr.filter((v) => v === a).length - arr.filter((v) => v === b).length)
					.pop();
			};

			const modus = mode(finishTypes);

			return ungroupedFinishes?.find((finish) => finish.code === modus)?.abstract_code;
		}

		if (ungroupedFinishes?.length === 1) {
			return ungroupedFinishes.at(0)?.abstract_code;
		}

		return "no_finishes";
	}, [ungroupedFinishes, workpiece.finishes.side]);

	const [selectedFinish, setSelectedFinish] = useState<string | undefined>(defaultFinish);

	const dimensionValues = useMemo(() => {
		const allowedTypes = [
			JsonInputType.Width,
			JsonInputType.WidthSection1,
			JsonInputType.WidthSection2,
			JsonInputType.Length,
			JsonInputType.LengthSection1,
			JsonInputType.LengthSection2,
			JsonInputType.LengthSection3,
			// Circles
			JsonInputType.Diameter,
			// Triangles
			JsonInputType.Basis,
			JsonInputType.Height,
			// 3D shapes
			JsonInputType.Height3d,
		];

		return Object.entries(workpiece.shape_dimensions)
			.filter(([key]) => allowedTypes.includes(key as JsonInputType))
			.map(([_key, value]) => value);
	}, [workpiece.shape_dimensions]);

	const shouldCheckCheckbox = useMemo(() => {
		// if there are no finishes the checkbox should be selected by default
		const sides = Object.values(workpiece.finishes.side);
		const finish = finishes.find((finish) => finish.abstract_code === selectedFinish);

		if (finish?.abstract_code === "absband") {
			return true;
		}

		if (sides.length === finish?.availableOnSides.length) {
			// check if all side options are the same
			const allFinishesEqual = Object.values(workpiece.finishes.side).every((value) =>
				isEqual(value, Object.values(workpiece.finishes.side)[0]),
			);

			return allFinishesEqual;
		}

		// check if smallest dimension is more than 100cm
		if (dimensionValues.every((value) => Number(value) > 1000)) {
			return !sides.length;
		}

		// check if any finish has more than 1 side
		if (finishes.some((finish) => finish.availableOnSides.length <= 1)) {
			return true;
		}

		return false;
	}, [dimensionValues, finishes, selectedFinish, workpiece.finishes.side]);

	const getSideLengthValue = useCallback((finish: Finish, variant: "max" | "min") => {
		const minValue = finish?.min_side_length;

		return Array.isArray(minValue)
			? variant === "max"
				? Math.max(...minValue)
				: Math.min(...minValue)
			: minValue;
	}, []);

	const hasLowestDimensions = useCallback(
		(finish: Finish) =>
			dimensionValues.every((value) => Number(value) >= (getSideLengthValue(finish, "min") ?? 0)),
		[dimensionValues, getSideLengthValue],
	);

	const hasHighestDimensions = useCallback(
		(finish: Finish) =>
			dimensionValues.some((value) => Number(value) >= (getSideLengthValue(finish, "max") ?? 0)),
		[dimensionValues, getSideLengthValue],
	);

	const currentFinish = useMemo(
		() => finishes?.find((finish) => finish.abstract_code === selectedFinish),
		[finishes, selectedFinish],
	);

	const isDisabled = useMemo(
		() =>
			currentFinish
				? !(hasLowestDimensions(currentFinish) && hasHighestDimensions(currentFinish))
				: true,
		[currentFinish, hasLowestDimensions, hasHighestDimensions],
	);

	const isDisabledByRightAngle = useMemo(
		() =>
			currentFinish?.abstract_code === "verstek"
				? currentFinish.availableOnSides.some((side) => disabledByRightAngle(side, true))
				: false,
		[currentFinish?.abstract_code, currentFinish?.availableOnSides, disabledByRightAngle],
	);

	const [checked, setChecked] = useState(shouldCheckCheckbox);

	const mandatory = useMemo(() => finishes.some((f) => f.mandatory), [finishes]);

	const onCheckedChange = useCallback(
		(checked?: boolean) => {
			setChecked((prev) => {
				const finish = finishes.find((finish) => finish.abstract_code === selectedFinish);

				// Only reset the finishes when going to all sides, this way the customer
				// can deselect some sides instead of having to click each side again
				if (checked !== false && prev === false && finish && finish.abstract_code !== "boorgat") {
					applyFinishOnSides(finish, finish.availableOnSides);
					const sides = Object.values(workpiece.finishes.side);

					if (sides.length && sides.every((value) => isEqual(value, sides[0]))) {
						// Apply finish option & parameters on all sides
						// if any finishOptions are selected and if all equal.
						// if not default values are already applied
						const fields = sides[0] ? Object.values(sides[0])[0] : undefined;

						if (fields) {
							fields.forEach((field, index) => {
								applyFinishOptionOnSides(
									finish,
									finish.availableOnSides,
									index,
									field.option,
									field.option_parameters,
								);

								Object.entries(field.option_parameters).map(([key, value], index) => {
									return applyFinishOptionParameterOnSides(
										finish.availableOnSides,
										index,
										key,
										String(value),
									);
								});
							});
						}
					}
				} else if (!finish?.mandatory) {
					reset();
				}

				return checked ?? !prev;
			});
		},
		[
			applyFinishOnSides,
			applyFinishOptionOnSides,
			applyFinishOptionParameterOnSides,
			finishes,
			reset,
			selectedFinish,
			workpiece.finishes.side,
		],
	);

	const onFinishChange = useCallback(
		async (value: string) => {
			setSelectedFinish(value);

			if (value === "no_finishes") {
				reset();
			} else {
				const finish = finishes.find((finish) => finish.abstract_code === value);

				if (finish) {
					const canApplyFinish = hasLowestDimensions(finish) && hasHighestDimensions(finish);

					const sideFinishes = finish.availableOnSides.filter(
						(side) => workpiece.finishes.side[side.sideCode],
					);

					const allFinishEqualAndCurrent = finish.availableOnSides.every((side) => {
						const finishOnSide = workpiece.finishes.side[side.sideCode];

						return finishOnSide
							? Object.keys(finishOnSide).some((key) => key.split("_").at(0) === value)
							: false;
					});

					const applyFinishAllowed = sideFinishes.length
						? sideFinishes.length === finish.availableOnSides.length
							? true
							: allFinishEqualAndCurrent
						: true;

					if (finish.abstract_code === "boorgat") {
						setChecked(false);
					} else if (finish.abstract_code === "absband") {
						setChecked(true);

						if (applyFinishAllowed && canApplyFinish && !mandatory) {
							if (finish.options.custom !== undefined) {
								applyEdgeBandOnSides(finish.availableOnSides);
							} else {
								applyFinishOnSides(finish, finish.availableOnSides);
							}
						}
					} else if (applyFinishAllowed) {
						setChecked(true);

						if (canApplyFinish && !mandatory) {
							applyFinishOnSides(finish, finish.availableOnSides);
						} else {
							reset();
						}
					} else {
						setChecked(false);
					}
				}
			}
		},
		[
			applyEdgeBandOnSides,
			applyFinishOnSides,
			finishes,
			hasHighestDimensions,
			hasLowestDimensions,
			mandatory,
			reset,
			workpiece.finishes.side,
		],
	);

	// Reset finishes if workpiece doesn't have the minimal dimensions
	// biome-ignore lint/correctness/useExhaustiveDependencies: onFinishChange can't be added to the dependencies
	useEffect(() => {
		if ((isDisabled || isDisabledByRightAngle) && !mandatory) {
			setWorkpiece((prev) => ({ ...prev, finishes: { side: {} } }));
		} else if (!hasWorkpieceFinishes && currentFinish) {
			void onFinishChange(currentFinish?.abstract_code);
		}
	}, [currentFinish, isDisabled, isDisabledByRightAngle, mandatory]);

	return (
		<Tabs.Root value={selectedFinish} onValueChange={onFinishChange}>
			<Tabs.List className="grid grid-cols-2 xs:grid-cols-3 gap-2 lg:grid-cols-2 xl:grid-cols-3">
				{finishes.map((finish) => (
					<TabsTrigger
						key={finish.code}
						variant="finishes"
						title={finish.label}
						value={finish.abstract_code}
						image={finish.main_image}
					/>
				))}
			</Tabs.List>

			{selectedFinish !== "no_finishes" && (
				<Fragment>
					{isDisabledByRightAngle && (
						<Alert variant="info" className="mt-4">
							<FormattedMessage
								defaultMessage="Om deze afwerking te kunnen toepassen, mag de lengte of breedte van de plaat niet groter zijn dan de maximale plaatafmeting min {value} per geselecteerde zijde."
								id="N78B6Z"
								values={{
									value: formatNumber({
										lang: intl.locale,
										value: 0.2,
										style: "unit",
										unit: "centimeter",
									}),
								}}
							/>
						</Alert>
					)}

					{isDisabled && currentFinish && !mandatory && (
						<Alert variant="info" className="mt-4">
							<div>
								<FormattedMessage
									defaultMessage="Zijdes moeten minimaal {length} x {width} zijn om {name} toe te passen"
									id="8yxdu4"
									values={{
										length: formatNumber({
											lang: intl.locale,
											value: (getSideLengthValue(currentFinish, "max") ?? 0) / 10,
											style: "unit",
											unit: "centimeter",
										}),
										width: formatNumber({
											lang: intl.locale,
											value: (getSideLengthValue(currentFinish, "min") ?? 0) / 10,
											style: "unit",
											unit: "centimeter",
										}),
										name: currentFinish.label,
									}}
								/>
							</div>

							<BackButton identifier={SHAPE_DIMENSIONS_STEP_IDENTIFIER}>
								<FormattedMessage defaultMessage="Bewerk afmetingen" id="WFYUlA" />
							</BackButton>
						</Alert>
					)}

					<div className="my-4">
						<Switch
							checked={checked}
							onCheckedChange={onCheckedChange}
							id="side-toggle"
							label={intl.formatMessage({
								defaultMessage: "Alle zijdes gelijk",
								id: "8C0pSp",
							})}
							disabled={
								isDisabled ||
								currentFinish?.abstract_code === "boorgat" ||
								currentFinish?.abstract_code === "absband" ||
								isDisabledByRightAngle
							}
						/>
					</div>

					{finishes.map((finish) => (
						<FinishesTab.Content
							key={finish.code}
							finish={finish}
							checked={checked}
							disabled={isDisabled}
						/>
					))}
				</Fragment>
			)}

			<DialogButton
				title={intl.formatMessage({ defaultMessage: "Ik zoek een andere afwerking", id: "3sCUsN" })}
				content={intl.formatMessage({
					defaultMessage:
						"In de plaatmateriaal configurator zitten specifiek per materiaal en dikte alle mogelijke opties verwerkt. Komt de gewenste vorm of afwerking niet naar voren in de configurator? Dan kunnen we dit ook niet produceren.",
					id: "6IBazc",
				})}
			>
				<Button variant="link" className="mt-4">
					{intl.formatMessage({ defaultMessage: "Ik zoek een andere afwerking", id: "3sCUsN" })}
				</Button>
			</DialogButton>
		</Tabs.Root>
	);
};

interface ContentComponentProps {
	finish: Finish;
	checked: boolean;
	disabled: boolean;
}

const ContentComponent: FC<ContentComponentProps> = ({ finish, checked, disabled }) => {
	const { disabledByRightAngle } = useFinishes();

	return (
		<Tabs.Content className="focus:outline-none" value={finish.abstract_code} tabIndex={-1}>
			{finish.abstract_code === "boorgat" ? (
				<FinishOptionDrillHoles finish={finish} disabled={disabled} />
			) : (
				<>
					{checked ? (
						<FinishOptionMultiple
							finish={finish}
							disabled={
								disabled ||
								(finish.abstract_code === "verstek"
									? finish.availableOnSides.some((side) => disabledByRightAngle(side, true))
									: false)
							}
						/>
					) : (
						<div className="space-y-4">
							{finish.availableOnSides
								.sort((a, b) => a.sideOrder - b.sideOrder)
								.map((side) => (
									<FinishOptionSingle
										key={side.sideCode}
										side={side}
										finish={finish}
										disabled={
											disabled ||
											(finish.abstract_code === "verstek"
												? disabledByRightAngle(side, false)
												: false)
										}
									/>
								))}
						</div>
					)}
				</>
			)}
		</Tabs.Content>
	);
};

const FinishesTab = Object.assign(FinishesTabComponent, {
	Content: ContentComponent,
});

export default FinishesTab;
